import React from 'react';
import { Property, UnitDetail } from '../../types/property';
import { formatCurrency } from '../../utils/formatting';

interface CashFlowBreakdownProps {
  property: Property;
}

export const CashFlowBreakdown: React.FC<CashFlowBreakdownProps> = ({ property }) => {
  if (!property.section8_rent || !property.expense_breakdown || !property.monthly_mortgage) {
    return null;
  }

  const { expense_breakdown, monthly_mortgage, section8_rent, monthly_cash_flow, unit_details } = property;
  const isMultiFamily = property.property_type === 'multi_family' && unit_details && unit_details.length > 0;

  const totalExpenses =
    monthly_mortgage +
    expense_breakdown.property_tax +
    expense_breakdown.insurance +
    expense_breakdown.maintenance +
    expense_breakdown.property_mgmt +
    expense_breakdown.capex_reserve +
    expense_breakdown.vacancy;

  const cashFlow = monthly_cash_flow || 0;
  const expensePct = Math.round((totalExpenses / section8_rent) * 100);
  const cashFlowPct = Math.max(0, Math.round((cashFlow / section8_rent) * 100));

  return (
    <div className="mt-3 space-y-3">

      {/* ── VISUAL SUMMARY BAR ── */}
      <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
        <div className="flex justify-between items-center mb-2 text-xs font-semibold text-gray-600 uppercase tracking-wide">
          <span>Income vs Expenses</span>
          <span className={cashFlow >= 0 ? 'text-green-600' : 'text-red-600'}>
            {cashFlow >= 0 ? '+' : ''}{formatCurrency(cashFlow)}/mo net
          </span>
        </div>

        {/* Stacked bar: green = income portion going to expenses, remaining = cash flow */}
        <div className="h-4 bg-gray-200 rounded-full overflow-hidden flex">
          <div
            className="h-full bg-red-400 transition-all"
            style={{ width: `${Math.min(expensePct, 100)}%` }}
          />
          {cashFlowPct > 0 && (
            <div
              className="h-full bg-green-500 transition-all"
              style={{ width: `${Math.min(cashFlowPct, 100)}%` }}
            />
          )}
        </div>
        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-red-400 inline-block"></span>Expenses {expensePct}%</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-green-500 inline-block"></span>Cash Flow {cashFlowPct}%</span>
          <span className="ml-auto font-medium text-gray-700">Rent: {formatCurrency(section8_rent)}/mo</span>
        </div>
      </div>

      {/* ── INCOME ── */}
      <div className="rounded-xl overflow-hidden border border-green-200">
        <div className="bg-green-600 px-3 py-2 flex items-center justify-between">
          <span className="text-xs font-bold text-white uppercase tracking-wide">📈 Monthly Income</span>
          <span className="text-sm font-bold text-white">{formatCurrency(section8_rent)}/mo</span>
        </div>
        <div className="bg-green-50 px-3 py-2">
          {isMultiFamily ? (
            <div className="space-y-1">
              {unit_details!.map((unit: UnitDetail) => (
                <div key={unit.unit} className="flex justify-between text-xs text-green-800">
                  <span>Unit {unit.unit} — {unit.bedrooms}BR/{unit.bathrooms}BA</span>
                  <span className="font-semibold">{formatCurrency(unit.rent || 0)}/mo</span>
                </div>
              ))}
              <div className="border-t border-green-200 pt-1 mt-1 flex justify-between text-xs font-bold text-green-900">
                <span>Total Estimated Rent</span>
                <span>{formatCurrency(section8_rent)}/mo</span>
              </div>
            </div>
          ) : (
            <div className="flex justify-between text-xs text-green-800 font-semibold">
              <span>Estimated Rent</span>
              <span>{formatCurrency(section8_rent)}/mo</span>
            </div>
          )}
        </div>
      </div>

      {/* ── EXPENSES ── */}
      <div className="rounded-xl overflow-hidden border border-red-200">
        <div className="bg-red-600 px-3 py-2 flex items-center justify-between">
          <span className="text-xs font-bold text-white uppercase tracking-wide">📉 Monthly Expenses</span>
          <span className="text-sm font-bold text-white">-{formatCurrency(totalExpenses)}/mo</span>
        </div>
        <div className="bg-red-50 px-3 py-2 space-y-1.5">
          {[
            { label: 'Principal + Interest (Mortgage)', value: monthly_mortgage },
            {
              label: `Property Tax${property.property_tax_rate
                ? ` (${(property.property_tax_rate * 100).toFixed(2)}% actual)`
                : ' (2.67% default)'}`,
              value: expense_breakdown.property_tax,
            },
            { label: 'Homeowners Insurance', value: expense_breakdown.insurance },
            { label: 'Maintenance', value: expense_breakdown.maintenance },
            { label: 'Property Management (10%)', value: expense_breakdown.property_mgmt },
            { label: 'CapEx Reserve (10%)', value: expense_breakdown.capex_reserve },
            { label: 'Vacancy Allowance (8%)', value: expense_breakdown.vacancy },
          ].map(({ label, value }) => (
            <div key={label} className="flex justify-between text-xs text-red-800">
              <span className="text-gray-600">{label}</span>
              <span className="font-semibold tabular-nums">-{formatCurrency(value)}</span>
            </div>
          ))}
          <div className="border-t border-red-200 pt-1.5 flex justify-between text-xs font-bold text-red-900">
            <span>Total Expenses</span>
            <span>-{formatCurrency(totalExpenses)}/mo</span>
          </div>
        </div>
      </div>

      {/* ── NET CASH FLOW ── */}
      <div className={`rounded-xl p-3 border-2 text-center ${
        cashFlow >= 0
          ? 'bg-green-100 border-green-400'
          : 'bg-red-100 border-red-400'
      }`}>
        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Monthly Cash Flow</div>
        <div className={`text-3xl font-black ${cashFlow >= 0 ? 'text-green-700' : 'text-red-700'}`}>
          {cashFlow >= 0 ? '+' : ''}{formatCurrency(cashFlow)}/mo
        </div>
        <div className="text-xs text-gray-500 mt-1">
          {formatCurrency(cashFlow * 12)}/yr · {expensePct}% of rent goes to expenses
        </div>
      </div>
    </div>
  );
};
